// The immersive Git client's 3-zone header toolbar (design 2.1 + 2.5):
//
//   [ workspace switcher ]   [        search        ]   [ action cluster ]
//        LEFT (auto)              CENTER (flex-1)            RIGHT (auto)
//
// LEFT is the GitWorkspaceSwitcher passed through as `switcher`. CENTER is a
// global search that filters the center graph's commit rows. RIGHT is a row of
// icon buttons grouped by separators: history (undo / redo), stash (stash /
// pop), network (fetch / pull / push) and refresh. This component is purely
// presentational toward git: every action just calls its handler; the parent
// (GitClient) owns the busy flag, toasts and the reflog undo/redo stacks. While
// `busy` (an action in flight) or `disabled` (no repo) every button is disabled;
// undo/redo additionally gate on `canUndo` / `canRedo`.

import type { ReactElement, ReactNode } from 'react';
import {
  Undo2,
  Redo2,
  Archive,
  ArchiveRestore,
  Download,
  ArrowDownToLine,
  ArrowUpFromLine,
  RefreshCw,
  type LucideIcon,
} from 'lucide-react';

export interface GitClientToolbarProps {
  /** The left-zone control (the <GitWorkspaceSwitcher/> element) passed through. */
  switcher: ReactNode;

  /** Center search query and its change handler (filters the center graph). */
  search: string;
  onSearch(q: string): void;

  /** True while any action is in flight; disables the whole action cluster. */
  busy: boolean;

  onRefresh(): void;
  onStash(): void;
  onPop(): void;
  onFetch(): void;
  onPull(): void;
  onPush(): void;

  /** Reflog history controls; undo/redo are independently gated. */
  undo: { canUndo: boolean; canRedo: boolean; onUndo(): void; onRedo(): void };

  /** True when no repository resolves; disables search + the whole cluster. */
  disabled?: boolean;
}

interface ActionButtonProps {
  icon: LucideIcon;
  label: string;
  onClick(): void;
  disabled: boolean;
}

function ActionButton({ icon: Icon, label, onClick, disabled }: ActionButtonProps): ReactElement {
  return (
    <button
      type="button"
      className="mp-iconbtn mp-git-toolbar__action"
      aria-label={label}
      title={label}
      disabled={disabled}
      onClick={onClick}
    >
      <Icon size={15} aria-hidden />
    </button>
  );
}

function Separator(): ReactElement {
  return <span className="mp-git-toolbar__sep" aria-hidden />;
}

export function GitClientToolbar({
  switcher,
  search,
  onSearch,
  busy,
  onRefresh,
  onStash,
  onPop,
  onFetch,
  onPull,
  onPush,
  undo,
  disabled = false,
}: GitClientToolbarProps): ReactElement {
  // Network / stash / refresh actions are unavailable with no repo or mid-op.
  const off = busy || disabled;

  return (
    <header className="mp-git-toolbar">
      <div className="mp-git-toolbar__left">{switcher}</div>

      <div className="mp-git-toolbar__center">
        <input
          type="search"
          className="mp-git-toolbar__search"
          aria-label="Search branches and commits"
          placeholder="Search branches and commits"
          value={search}
          onChange={(e) => onSearch(e.target.value)}
          disabled={disabled}
        />
      </div>

      <div className="mp-git-toolbar__actions">
        <ActionButton
          icon={Undo2}
          label="Undo last commit"
          onClick={undo.onUndo}
          disabled={off || !undo.canUndo}
        />
        <ActionButton
          icon={Redo2}
          label="Redo commit"
          onClick={undo.onRedo}
          disabled={off || !undo.canRedo}
        />
        <Separator />
        <ActionButton icon={Archive} label="Stash changes" onClick={onStash} disabled={off} />
        <ActionButton
          icon={ArchiveRestore}
          label="Pop latest stash"
          onClick={onPop}
          disabled={off}
        />
        <Separator />
        <ActionButton icon={Download} label="Fetch" onClick={onFetch} disabled={off} />
        <ActionButton icon={ArrowDownToLine} label="Pull" onClick={onPull} disabled={off} />
        <ActionButton icon={ArrowUpFromLine} label="Push" onClick={onPush} disabled={off} />
        <Separator />
        <ActionButton
          icon={RefreshCw}
          label="Refresh repository"
          onClick={onRefresh}
          disabled={disabled}
        />
      </div>
    </header>
  );
}
